import { useGetOfferCustomFields } from "api/TenantSlug/OfferCustomFields/hooks.ts";
import {
    AddCustomInformationFieldDrawer
} from "components/Drawers/AddCustomInformationFieldDrawer/AddCustomInformationFieldDrawer.tsx";
import { Table } from "components/UI/Table";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations.ts";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useUser } from "hooks/useUser.ts";
import { FC, Key } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums.ts";
import { DrawerId, TableId } from "types/models.ts";

import OfferCustomFieldData = App.Data.OfferCustomFieldData;
import GetOfferCustomFieldsRequest = App.Http.Requests.Offer.GetOfferCustomFieldsRequest;
import GetOfferCustomFieldsResponse = App.Http.Responses.Offer.GetOfferCustomFieldsResponse;


interface Props {
    offerId: string;
    readOnly?: boolean;
}

export const OfferInformationTable: FC<Props> = ({ offerId, readOnly = false }) => {
    const { t: tG } = usePaldockTranslation("tables.general-columns")
    const user = useUser()
    const tableAtomInstance = tableAtom<OfferCustomFieldData>();
    const tableState = useRecoilValue(tableAtomInstance(TableId.OfferInformationTable));
    const customFieldsQuery = useGetOfferCustomFields(offerId, { filter: { filter: { type: 'information',  } },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        }, })
    const tableFetchDataUtils = useTableDataApiOperations<
        GetOfferCustomFieldsResponse,
        GetOfferCustomFieldsRequest,
        OfferCustomFieldData
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
    >(TableId.OfferInformationTable, customFieldsQuery);

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<OfferCustomFieldData>({
        entityName: "information",
        detailDrawerId: DrawerId.AddCustomInformationField,
        detailDrawerParam: "customFieldId",
        detailPermission: PermissionEnum.OfferCustomFieldCreate,
        editPermission: PermissionEnum.OfferCustomFieldUpdate,
    });

    const onSubmit = () => {
        tableFetchDataUtils.refetchData()
    }

    const columnConfig: TableColumnType<OfferCustomFieldData>[] = [
        {
            title: tG('name'),
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            width: 150,
        },
        {
            title: tG('value'),
            dataIndex: 'value',
            key: 'value',
            sorter: true,
            ellipsis: true,
            width: 200,
        },
    ]

    return (
        <>
            <AddCustomInformationFieldDrawer offerId={offerId} readOnly={readOnly} onSubmit={onSubmit} />
            <Table<GetOfferCustomFieldsResponse, OfferCustomFieldData>
                tableId={TableId.OfferInformationTable}
                columnConfig={columnConfig}
                dataSource={tableFetchDataUtils.tableData?.offerCustomFields || []}
                data={tableFetchDataUtils.tableData as GetOfferCustomFieldsResponse}
                loading={!tableFetchDataUtils.fetchQuery.data?.offerCustomFields}
                onRefresh={tableFetchDataUtils.refetchData}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                showRefetchButton={false}
                addEntityData={!readOnly && !user.isAffiliate() ? {
                    permission: PermissionEnum.OfferCustomFieldCreate,
                    drawerId: DrawerId.AddCustomInformationField,
                } : undefined}
                actionsLabel={readOnly ? null : undefined}
                autoScrollWidth={true}
                enableTopBar={false}
            />
        </>
    )
}
